// FILE: src/pages/ResetPasswordPage.jsx
// Set a new password from an emailed link. The same policy as registration
// (MTO glossary "Strong Password", D10); a reset never unlocks a locked
// account (MTO 1.3.4: only support can).
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Link, Stack, TextField, Typography } from "@mui/material";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { apiFetch } from "../services/api";
import { messageFor } from "../i18n/errors";
import { passwordFailures } from "../utils/passwordPolicy";
import PasswordRules from "../components/PasswordRules";
import PublicLayout from "../layouts/PublicLayout";
import { LoadingState } from "../components/ui";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState(null); // plain-language message
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { accountLocked }

  useEffect(() => {
    if (!token) {
      setError(messageFor("TOKEN_NOT_FOUND"));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await apiFetch(`/api/auth/verify-reset-password?token=${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.code || "INVALID_TOKEN");
        setTokenValid(true);
      } catch (err) {
        setError(messageFor(/^[A-Z_]+$/.test(err.message) ? err.message : "NETWORK"));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const weak = passwordFailures(newPassword).length > 0;
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (weak || newPassword !== confirmPassword) return;
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.code || "VALIDATION_FAILED");
      setDone({ accountLocked: !!data.accountLocked });
    } catch (err) {
      setError(messageFor(/^[A-Z_]+$/.test(err.message) ? err.message : "NETWORK"));
    } finally {
      setSubmitting(false);
    }
  }

  let body;
  if (loading) {
    body = <LoadingState variant="inline" label="Checking your reset link…" />;
  } else if (!tokenValid) {
    body = (
      <>
        <Alert severity="error" sx={{ my: 2 }} role="alert">
          {error || messageFor("INVALID_TOKEN")}
        </Alert>
        <Link component={RouterLink} to="/forgot-password">
          Ask for a new reset link
        </Link>
      </>
    );
  } else if (done) {
    body = (
      <>
        <Alert severity={done.accountLocked ? "warning" : "success"} sx={{ my: 2 }} role="status">
          {done.accountLocked
            ? "Your password has been changed. Your account is still locked: contact student support to unlock it."
            : "Your password has been changed. You can sign in with it now."}
        </Alert>
        <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
          Go to sign in
        </Button>
      </>
    );
  } else {
    body = (
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>Choose a new password you haven't used here before.</Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {error}
          </Alert>
        )}
        <Stack spacing={2.5}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            fullWidth
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={touched && weak}
            helperText={touched && weak ? messageFor("WEAK_PASSWORD") : " "}
            inputProps={{ "aria-describedby": "password-rules" }}
          />
          <PasswordRules password={newPassword} />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            fullWidth
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={(touched || mismatch) && newPassword !== confirmPassword}
            helperText={(touched || mismatch) && newPassword !== confirmPassword ? "The two passwords don't match." : " "}
          />
          <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
            {submitting ? <CircularProgress size={24} color="inherit" aria-label="Saving" /> : "Set new password"}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <PublicLayout>
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Set a new password
          </Typography>
          {body}
        </CardContent>
      </Card>
    </PublicLayout>
  );
}
